import { observer } from "mobx-react-lite";
import type { InputRequestValue } from "@/components/active-session/types";
import { JsonObject } from "@/domains/common/types";
import { ImportMarkdownForm } from "./input-markdown-form";

interface InputFormProps {
  inputRequestValue: InputRequestValue;
  dismissSession: () => void;
  metadata: JsonObject;
}

export const REGISTERED_FORMS = ["IMPORT_MARKDOWN"] as const;
export type InputFormKind = (typeof REGISTERED_FORMS)[number];

export const canRenderForm = (form?: string) => {
  if (!form) return false;
  return REGISTERED_FORMS.includes(form as InputFormKind);
};

/**
 * Session events can include INPUT_REQUEST events which include a `form` property.
 * The Session component renders the form for the Input Requests that don't have a corresponding
 * Input Response event. It checks `canRenderForm` and will skip the form if it returns false to avoid
 * any mismatch in the session data with this client's ability to render the form. Otherwise, the Session could
 * get "stuck" for the user.
 *
 * InputForm renders the form component associated with the form type. Each form component should handle its own
 * UI and logic. When the user provides an answer, the form should create an `INPUT_RESPONSE` event in the session
 * that responds to the request at which point the session will close the form.
 * The form may call the `dismissSession` callback to completely dismiss the session, for instance if the user clicks
 * a "cancel" button that should cancel the entire Session.
 */

/**
 * @deprecated - Use the newer MDS components instead.
 *
 * - If something doesn't exist yet, please create a new MDS component and
 *   have it reviewed by the team!
 *
 * - If something needs to hook into our `stores`, add it to the /components directory.
 *
 * - If something is a one-off; please colocate it with the component/page that
 *   needs it.
 *
 * - If you have questions, please ask in the #hub-eng-web-team Slack channel.
 */
export const InputForm = observer(({ inputRequestValue, dismissSession }: InputFormProps) => {
  switch (inputRequestValue.form) {
    case "IMPORT_MARKDOWN":
      return (
        <ImportMarkdownForm
          requestId={inputRequestValue.id}
          sessionId={inputRequestValue.metadata.session_id as string}
          dismissImportSession={dismissSession}
          metadata={inputRequestValue.metadata}
        />
      );

    default:
      console.warn(`Un-implemented input form: ${inputRequestValue.form}`);
      return <div>Coming soon...</div>;
  }
});
